// src/stores/caseStore.ts
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  type CasesPartialUpdateData,
  CasesService,
  type CustomerCase as Case,
  type CustomerCaseDataChange,
  type CustomerEntity,
  type CustomerEntityWithRelationship,
  EntitiesService,
  type GenericDataMapper,
  type LawData,
  AiAugmentedByMapperKeysEnum,
  type CustomerCaseData,
  RelationshipTypeEnum
} from '@/types/generated'
import { emitter } from './eventEmitter'
import { useDocumentModule } from './documentModule'

// Define the store function
export const useCaseStore = defineStore('caseStore', () => {
  const caseRecord = ref<Case | null>(null)
  const isLoadingCase = ref<boolean>(false)
  const caseLoadError = ref<Error | null>(null)

  const documentModule = useDocumentModule(caseRecord)

  // Actions (asynchronous actions such as fetching data)
  async function fetchCase(uuid: string) {
    caseRecord.value = (await CasesService.casesRetrieve({ uuid: uuid })) as Case

    // TODO: We don't want to automatically poll for documents on page refresh
    // This can lead to flaky behavior and unexpected state changes
    // documentModule.initAllDocumentsTracking()
  }

  // New method with error handling and loading state tracking
  async function fetchCaseWithErrorHandling(uuid: string) {
    isLoadingCase.value = true
    caseLoadError.value = null

    try {
      caseRecord.value = (await CasesService.casesRetrieve({ uuid: uuid })) as Case

      // TODO: We don't want to automatically poll for documents on page refresh
      // This can lead to flaky behavior and unexpected state changes
      // documentModule.initAllDocumentsTracking()

      return caseRecord.value
    } catch (error) {
      console.error('Error fetching case:', error)
      caseLoadError.value = error instanceof Error ? error : new Error(String(error))
      throw error
    } finally {
      isLoadingCase.value = false
    }
  }

  const lawSpecificMappers = computed<GenericDataMapper[]>(() => {
    return caseRecord.value?.data.mappers.filter(
      (mapper) =>
        mapper.is_law_specific_field && mapper.law_field !== undefined && mapper.law_field !== null
    ) as GenericDataMapper[]
  })

  const nonEmptyLawSpecificMappers = computed<GenericDataMapper[]>(() => {
    return lawSpecificMappers.value.filter((mapper) => {
      const [parent, field] = mapper.key.split('.') as [keyof LawData, string]
      // Type assertion since we know parent will be 'law' for law specific fields
      const parentData = caseRecord.value?.data?.[parent as 'law']
      if (!parentData || typeof parentData !== 'object') return false

      // Access the nested field
      const value = (parentData as any)[field]

      // Check for empty values
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === 'object' && Object.keys(value).length === 0) return false

      return true
    })
  })

  async function loadCase(uuid: string) {
    documentModule.stopAllDocumentPolling()
    documentModule.clearDocumentProcessingStates()

    await fetchCase(uuid)
  }

  function updateEntity(entityUuid: string, updates: Record<string, any>) {
    if (!caseRecord.value?.data.entities) return

    const entityIndex = caseRecord.value.data.entities.findIndex(
      (e) => e.entity?.uuid === entityUuid
    )
    if (entityIndex === -1) return

    Object.assign(caseRecord.value.data.entities[entityIndex].entity!, updates)
  }

  async function addEntity(
    entity: CustomerEntity,
    relationshipType: RelationshipTypeEnum = RelationshipTypeEnum.ENTITIES_OTHER
  ) {
    const response = await EntitiesService.entitiesCreate({
      requestBody: entity
    })
    const relationshipResponse = await EntitiesService.entitiesAddCaseDataRelationshipCreate({
      uuid: response.uuid,
      requestBody: {
        case_data_uuid: caseRecord.value?.data.uuid as string,
        relationship_type: relationshipType
      }
    })
    caseRecord.value?.data.entities.push(relationshipResponse as CustomerEntityWithRelationship)
    return relationshipResponse
  }

  async function deleteEntity(entityRelationShip: CustomerEntityWithRelationship) {
    if (!entityRelationShip || !entityRelationShip.entity || !entityRelationShip.entity.uuid) return

    // Check if the entity has relationships
    if (!entityRelationShip.relationships || entityRelationShip.relationships.length === 0) {
      await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
        uuid: entityRelationShip.entity.uuid,
        requestBody: {
          case_data_uuid: caseRecord.value?.data.uuid as string,
          remove_case_data_association_if_no_relationships_exist: true
        }
      })
    } else {
      for (const relationship of entityRelationShip.relationships) {
        await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
          uuid: entityRelationShip.entity.uuid,
          requestBody: {
            case_data_uuid: caseRecord.value?.data.uuid as string,
            relationship_uuid: relationship.uuid as string,
            remove_case_data_association_if_no_relationships_exist: true
          }
        })
      }
    }

    // Then we load the case again to get fresh data
    await loadCase(caseRecord.value?.uuid as string)
  }

  function updateCaseChanges(changes: CustomerCaseDataChange[]) {
    if (caseRecord.value) {
      caseRecord.value = {
        ...caseRecord.value,
        data: {
          ...caseRecord.value.data,
          changes
        }
      }
    }
  }

  function getDataWithMapperKey(mapperKey: AiAugmentedByMapperKeysEnum, entityUuid?: string) {
    // TODO: implement this for the entities using the entityUuid
    if (!mapperKey) {
      console.warn('Attempted to get data with undefined mapper key')
      return undefined
    }
    console.log('attempting to get', mapperKey)
    const parts = (mapperKey || '').split('.')
    if (parts.length !== 2) {
      console.warn('Invalid mapper key format:', mapperKey)
      return undefined
    }

    const [parent, field] = parts
    // Safely access the data using the parent key first
    const parentData = caseRecord.value?.data?.[parent as keyof CustomerCaseData]

    // Then access the field on the parent data if it exists
    if (parentData && typeof parentData === 'object') {
      return (parentData as any)[field]
    }

    return undefined
  }

  function updateDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    // TODO: implement this for the entities using the entityUuid
    console.log('updateDataWithMapper', mapperKey)
  }

  async function saveDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    // TODO: implement this for the entities using the entityUuid
    updateDataWithMapperKey(mapperKey, value)
    const parts = mapperKey.split('.')
    if (parts.length !== 2) {
      console.warn('Invalid mapper key format:', mapperKey)
      return
    }

    const [parent, field] = parts

    // Check if caseRecord exists
    if (!caseRecord.value) {
      console.warn('Cannot save data: caseRecord is null')
      return
    }

    // Check if there are any actual changes on that field
    const parentData = caseRecord.value.data?.[parent as keyof CustomerCaseData]
    if (parentData && typeof parentData === 'object' && (parentData as any)[field] === value) {
      console.log('no changes to save on ' + mapperKey + ', not saving')
      return
    }

    // Create the data object with the nested structure
    const dataToUpdate = {
      data: { [parent]: { [field]: value } }
    }

    // Use type assertion to match the expected CasesPartialUpdateData type
    const updateData = {
      uuid: caseRecord.value.uuid as string,
      requestBody: dataToUpdate
    } as unknown as CasesPartialUpdateData

    const result: Case = (await CasesService.casesPartialUpdate(updateData)) as Case

    // Update the case changes through the store
    if (result.data.changes) {
      updateCaseChanges(result.data.changes)
    }

    // Update the local state directly to ensure UI is in sync
    if (caseRecord.value && caseRecord.value.data) {
      // Ensure the parent object exists
      if (!caseRecord.value.data[parent as keyof CustomerCaseData]) {
        ;(caseRecord.value.data as any)[parent] = {}
      }

      // Update the field value
      const parentObj = caseRecord.value.data[parent as keyof CustomerCaseData]
      if (parentObj && typeof parentObj === 'object') {
        ;(parentObj as any)[field] = value
      }

      // Emit an event to notify components that data has been updated
      emitter.emit('data-updated', {
        key: parent,
        mapperKey: mapperKey,
        value: value
      })
    }
  }

  // Return state, getters and actions
  return {
    caseRecord,
    isLoadingCase,
    caseLoadError,
    lawSpecificMappers,
    nonEmptyLawSpecificMappers,
    loadCase,
    fetchCaseWithErrorHandling,
    updateEntity,
    updateCaseChanges,
    getDataWithMapperKey,
    updateDataWithMapperKey,
    saveDataWithMapperKey,
    addEntity,
    deleteEntity,
    ...documentModule
  }
})
